import React, { useContext } from 'react'
import { Link } from 'gatsby'
import { css, useTheme } from '@emotion/react'
import Container from './container'
import SearchInput from './search-input'
import logo from '../images/svgs/moog-logo.svg'
import logo2 from '../images/svgs/moog-messenger-logo.svg'
import account from '../images/svgs/account-icon-white.svg'
import externalLink from '../images/svgs/external-link-white.svg'
import hamburgerIconWhite from '../images/svgs/hamburger-icon-white.svg'
import xCloseWhite from '../images/svgs/x-close-white.svg'
import AppContext from './context'

const navbarBarStyle = {
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    backdropFilter: 'blur(10px)',
}

const navStyle = theme => css({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'stretch',
    fontFamily: theme.fonts.secondary,
})

const navItemsStyle = {
    display: 'flex',
    gap: 40,
    '& > *': {
        display: 'flex',
        alignItems: 'center',
        fontWeight: 700,
    },
    '@media(max-width: 1024px)': {
        display: 'none',
    }
}

const searchAccountStyle = {
    display: 'flex',
    alignItems: 'stretch',
    flexShrink: 1,
    transition: 'all .25s linear',
}

export default function NavBar(){
    const { overlay, setOverlay, subMenu, setSubmenu, clientWidth } = useContext(AppContext)
    const theme = useTheme()

    const delayHover = e => {
        if(typeof window === `undefined`) return
        
        const el = e.target.closest('a') // in case external link img is hovered
        let overlayTimeout

        const clearOverlayTimeout = () => {
            clearTimeout(overlayTimeout)
            window.removeEventListener('navigate', clearOverlayTimeout)
            el.removeEventListener('mouseleave', clearOverlayTimeout)
        }

        window.addEventListener('navigate', clearOverlayTimeout)
        el.addEventListener('mouseleave', clearOverlayTimeout)
        
        overlayTimeout = setTimeout(() => {
            // https://developer.mozilla.org/en-US/docs/Web/API/Element/matches
            if (el.matches(':hover')) {
                setOverlay(el.innerText)
            }
            window.removeEventListener('navigate', clearOverlayTimeout)
            el.removeEventListener('mouseleave', clearOverlayTimeout)
        }, 300)
    }

    const isSearch = overlay === 'Search'
    const isAccount = overlay === 'Account'
    const isSubMenuAccount = subMenu === 'Account'
    const isMobile = overlay === 'Mobile'
    
    return(
        <Container id='navbar' fluid style={navbarBarStyle}>
            <Container>
                <div css={navStyle}
                    onMouseMove={e => {
                        if(!overlay) return
                        if(isSearch || isAccount || isMobile) return
                        if(e.target.closest('#navbar-category-links')) return
                        
                        setOverlay('')
                    }}
                >
                    {/* LOGO */}
                    <Link to='/'
                        css={{
                            padding: 16,
                            '@media(max-width: 425px)': {
                                display: isSearch ? 'none' : 'inline-block'
                            }
                        }}
                    >
                        <img src={logo2} height="30" alt="Moog Music" />
                    </Link>

                    {/* SYNTHESIZERS | ACCESSORIES | SOFTWARE STORE | THE WAVE | SUPPORT */}
                    {!isSearch && <div id='navbar-category-links' css={navItemsStyle}>
                        <Link
                            to='/synthesizers'
                            onMouseEnter={delayHover}
                            css={{ color: overlay === 'Synthesizers' ? theme.colors.primary : 'white' }}
                        >
                            Synthesizers
                        </Link>
                        <Link
                            to='/accessories' 
                            onMouseEnter={delayHover}
                            css={{ color: overlay === 'Accessories' ? theme.colors.primary : 'white' }}
                        >
                            Accessories
                        </Link>

                        <a href='https://software.moogmusic.com/' 
                            target='_blank' 
                            rel='noreferrer' 
                            onMouseEnter={delayHover}
                            css={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8, color: overlay === 'Software Store' ? theme.colors.primary : 'white' }}
                        >
                            Software Store
                            <img src={externalLink} alt='external link' height='8' />
                        </a>

                        <Link
                            to='/the-wave' 
                            onMouseEnter={delayHover}
                            css={{ color: overlay === 'The Wave' ? theme.colors.primary : 'white' }}
                        >
                            The Wave
                        </Link>

                        <Link
                            to='/support'
                            onMouseEnter={delayHover}
                            css={{ color: overlay === 'Support' ? theme.colors.primary : 'white' }}
                        >
                            Support
                        </Link>
                    </div>}

                    {/* SEARCH | ACCOUNT | MOBILE */}
                    <div
                        css={searchAccountStyle}
                        style={isSearch ? { flexGrow: 1 } : { flexGrow: 0 } }
                    >
                        {/* SEARCH */}
                        <div css={{ display: 'flex', alignItems: 'center', flexGrow: 1, }}>
                            <SearchInput onClick={() => setOverlay('Search')} overlay={overlay} />
                        </div>

                        {/* ACCOUNT */}
                        {(!isSearch && !isAccount && !isSubMenuAccount) && <button
                            onClick={() => {
                                if(clientWidth < 1024){
                                    setOverlay('Mobile')
                                    setSubmenu('Account')
                                } else {
                                    setOverlay('Account')
                                }
                            }}
                            css={{
                                padding: 0,
                                width: 48,
                                background: 'transparent',
                                border: 'none',
                                '&:hover': {
                                    cursor: 'pointer',
                                }
                            }}
                        >
                            <img src={account} alt="Account" />
                        </button>}

                        {/* X */}
                        {(isSearch || isAccount || isMobile) && <button
                            onClick={() => setOverlay('')}
                            css={{
                                padding: 0,
                                width: 48,
                                background: 'transparent',
                                border: 'none',
                                '&:hover': {
                                    cursor: 'pointer',
                                }
                            }}
                        >
                            <img src={xCloseWhite} alt='Mobile menu' />
                        </button>}

                        {/* HAMBURGER */}
                        {(isSubMenuAccount || (!isMobile && !isSearch)) && <button
                            id='hamburger'
                            onClick={() => {
                                if(isSubMenuAccount) setSubmenu('')
                                else setOverlay('Mobile')
                            }}
                            css={{
                                padding: 0,
                                width: 48,
                                background: 'transparent',
                                border: 'none',
                                '@media(min-width: 1025px)': {
                                    display: 'none',
                                },
                                '&:hover': {
                                    cursor: 'pointer',
                                }
                            }}
                        >
                            <img src={hamburgerIconWhite} alt='Mobile menu' />
                        </button>}
                    </div>
                </div>
            </Container>
        </Container>
    )
}